import { useCallback, useState, useEffect } from 'react'
import { gql, useQuery } from '@apollo/client'
import { sortDateDescendant } from '~/utilities/datetimeFormats'

const getProjectsCache = (): ProjectsQueryResponse | null => {
  try {
    const cached = localStorage.getItem('mbue_cache_projects_data')
    if (!cached) return null
    const entry = JSON.parse(cached) as {
      data: ProjectsQueryResponse
      timestamp: number
      version: string
    }
    // Check if cache is expired (5 minutes)
    if (Date.now() - entry.timestamp > 5 * 60 * 1000) {
      localStorage.removeItem('mbue_cache_projects_data')
      return null
    }
    return entry.data
  } catch (error) {
    console.error('Failed to get projects cache:', error)
    return null
  }
}

const setProjectsCache = (data: ProjectsQueryResponse) => {
  try {
    const entry = {
      data,
      timestamp: Date.now(),
      version: '1.0.0'
    }
    localStorage.setItem('mbue_cache_projects_data', JSON.stringify(entry))
  } catch (error) {
    console.error('Failed to set projects cache:', error)
  }
}

const removeProjectsCache = () => {
  try {
    localStorage.removeItem('mbue_cache_projects_data')
  } catch (error) {
    console.error('Failed to remove projects cache:', error)
  }
}

const hasValidProjectsCache = (): boolean => {
  return getProjectsCache() !== null
}

const getProjectsCacheAge = (): number | null => {
  try {
    const cached = localStorage.getItem('mbue_cache_projects_data')
    if (!cached) return null
    const entry = JSON.parse(cached) as {
      data: ProjectsQueryResponse
      timestamp: number
      version: string
    }
    return Date.now() - entry.timestamp
  } catch (error) {
    return null
  }
}

export interface Project {
  id: number
  orgId: number
  orgName: string
  name: string
  thumbnail: string | null
  createdAt: string
  jobs: {
    id: number
    createdAt: string
  }[]
}

export interface ProjectsQueryResponse {
  projects: Project[]
}

const PROJECTS_QUERY = gql`
  query ProjectsQuery {
    projects {
      id
      orgId
      orgName
      name
      thumbnail
      createdAt
      jobs {
        id
        createdAt
      }
    }
  }
`

const useProjectsQuery = (orgId: number) => {
  const [cachedData, setCachedData] = useState<ProjectsQueryResponse | null>(null)
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true)
  const [cacheError, setCacheError] = useState<Error | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    const loadCachedData = () => {
      try {
        const cached = getProjectsCache()
        if (cached) {
          setCachedData(cached)
          setCacheError(null)
          console.log('Loaded projects data from cache')
        }
      } catch (error) {
        console.error('Failed to load cached projects data:', error)
        setCacheError(error instanceof Error ? error : new Error('Unknown cache error'))
      } finally {
        setIsLoadingFromCache(false)
      }
    }

    loadCachedData()
  }, [])

  const { data, loading, error, refetch } = useQuery<ProjectsQueryResponse>(
    PROJECTS_QUERY,
    {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      skip: orgId < 0 || isLoadingFromCache,
      onCompleted: (newData) => {
        if (newData) {
          try {
            setProjectsCache(newData)
            setCachedData(newData)
            setCacheError(null)
            setIsRefreshing(false)
            console.log('Updated projects data from network')
          } catch (error) {
            console.error('Failed to update projects cache:', error)
          }
        }
      },
      onError: (error) => {
        console.error('Projects data query error:', error)
        setCacheError(error)
        setIsRefreshing(false)
      }
    }
  )

  const refetchProjects = useCallback(async () => {
    if (!orgId) return

    try {
      setIsRefreshing(true)
      const result = await refetch()
      if (result.data) {
        setProjectsCache(result.data)
        setCachedData(result.data)
        setCacheError(null)
      }
      return result
    } catch (error) {
      console.error('Failed to refetch projects data:', error)
      setCacheError(error as Error)
      throw error
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch, orgId])

  const refetchFromNetwork = useCallback(async () => {
    if (!orgId) return

    try {
      setIsRefreshing(true)
      const result = await refetch({
        fetchPolicy: 'network-only'
      })
      if (result.data) {
        setProjectsCache(result.data)
        setCachedData(result.data)
        setCacheError(null)
      }
      return result
    } catch (error) {
      console.error('Failed to refetch projects data from network:', error)
      setCacheError(error as Error)
      throw error
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch, orgId])

  const invalidateCache = useCallback(async () => {
    try {
      removeProjectsCache()
      setCachedData(null)
      setCacheError(null)
      await refetchProjects()
    } catch (error) {
      console.error('Failed to invalidate projects cache:', error)
      throw error
    }
  }, [refetchProjects])

  const clearCache = useCallback(() => {
    try {
      removeProjectsCache()
      setCachedData(null)
      setCacheError(null)
    } catch (error) {
      console.error('Failed to clear projects cache:', error)
    }
  }, [])

  const isCached = useCallback((): boolean => {
    try {
      return hasValidProjectsCache()
    } catch (error) {
      console.error('Failed to check projects cache status:', error)
      return false
    }
  }, [])

  const getCacheAge = useCallback((): number | null => {
    try {
      return getProjectsCacheAge()
    } catch (error) {
      console.error('Failed to get projects cache age:', error)
      return null
    }
  }, [])

  const currentData = cachedData ?? data
  const currentLoading = isLoadingFromCache || (loading && !cachedData)
  const currentError = cacheError ?? error

  const projects =
    orgId > 0 && currentData
      ? currentData.projects
          .filter((project) => project.orgId === orgId)
          .toSorted((a, b) => sortDateDescendant(a.createdAt, b.createdAt))
      : []

  return {
    projects,
    loading: currentLoading,
    error: currentError,
    refetchProjects,
    refetchFromNetwork,
    invalidateCache,
    clearCache,
    isCached,
    getCacheAge,
    isUsingCache: !!cachedData && !data,
    isRefreshing,
    cacheAge: getCacheAge(),
    dataSource: cachedData ? (data ? 'cache-then-network' : 'cache-only') : 'network-only'
  }
}

export default useProjectsQuery
